import { useState, useEffect } from 'react';
import {
  FRAMEWORKS_BY_CATEGORY,
  CATEGORY_META,
  FRAMEWORK_LIST,
  type FrameworkId,
  type FrameworkCategory,
} from '@seedgovernance/shared';
import { useWorkspace } from '../../hooks/useWorkspace';
import { apiClient } from '../../lib/api';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Link } from 'react-router';

interface ActiveFramework {
  frameworkId: FrameworkId;
  activatedAt: string;
}

export function FrameworkList() {
  const { currentWorkspace } = useWorkspace();
  const [activeFrameworks, setActiveFrameworks] = useState<Set<FrameworkId>>(new Set());
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState<FrameworkId | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tierLimit] = useState<number | null>(null);

  useEffect(() => {
    if (!currentWorkspace) return;
    fetchActiveFrameworks();
  }, [currentWorkspace]);

  async function fetchActiveFrameworks() {
    if (!currentWorkspace) return;
    setLoading(true);
    setError(null);

    const { data, error: apiError } = await apiClient.get<ActiveFramework[]>(
      `/api/tenants/${currentWorkspace.tenantId}/frameworks`
    );

    if (apiError) {
      setError(apiError.message);
    } else if (data) {
      setActiveFrameworks(new Set(data.map((f) => f.frameworkId)));
    }
    setLoading(false);
  }

  async function activateFramework(frameworkId: FrameworkId) {
    if (!currentWorkspace) return;
    setToggling(frameworkId);

    const { error: apiError } = await apiClient.post(
      `/api/tenants/${currentWorkspace.tenantId}/frameworks`,
      { frameworkId }
    );

    if (apiError) {
      setError(apiError.message);
    } else {
      setActiveFrameworks((prev) => new Set([...prev, frameworkId]));
    }
    setToggling(null);
  }

  async function deactivateFramework(frameworkId: FrameworkId) {
    if (!currentWorkspace) return;
    setToggling(frameworkId);

    const { error: apiError } = await apiClient.delete(
      `/api/tenants/${currentWorkspace.tenantId}/frameworks/${frameworkId}`
    );

    if (apiError) {
      setError(apiError.message);
    } else {
      setActiveFrameworks((prev) => {
        const next = new Set(prev);
        next.delete(frameworkId);
        return next;
      });
    }
    setToggling(null);
  }

  function handleToggle(frameworkId: FrameworkId) {
    if (activeFrameworks.has(frameworkId)) {
      deactivateFramework(frameworkId);
    } else {
      activateFramework(frameworkId);
    }
  }

  const activeCount = activeFrameworks.size;
  const totalCount = FRAMEWORK_LIST.length;
  const atLimit = tierLimit !== null && activeCount >= tierLimit;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <svg className="w-8 h-8 animate-spin text-brand" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-display font-bold text-white">Frameworks</h1>
        <p className="text-slate-400 text-sm mt-1">
          {activeCount} of {totalCount} active
        </p>
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {/* Framework cards grouped by category */}
      <div className="space-y-8">
        {(Object.entries(FRAMEWORKS_BY_CATEGORY) as [FrameworkCategory, typeof FRAMEWORKS_BY_CATEGORY[FrameworkCategory]][]).map(
          ([category, frameworks]) => (
            <div key={category}>
              <div className="mb-3">
                <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">
                  {CATEGORY_META[category]?.label || category}
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  {CATEGORY_META[category]?.description}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {frameworks.map((fw) => {
                  const isActive = activeFrameworks.has(fw.id);
                  const isToggling = toggling === fw.id;
                  const cannotActivate = !isActive && atLimit;

                  return (
                    <div
                      key={fw.id}
                      className={`bg-surface-mid rounded-xl border p-4 transition-colors ${
                        isActive
                          ? 'border-brand'
                          : 'border-border'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <Link
                          to={`/frameworks/${fw.id}`}
                          className="flex items-center gap-2.5 min-w-0 group"
                        >
                          <div
                            className="w-3 h-3 rounded-full shrink-0"
                            style={{ backgroundColor: fw.color }}
                          />
                          <div className="min-w-0">
                            <span className="text-sm font-medium text-white group-hover:text-brand-light transition-colors block truncate">
                              {fw.shortName}
                            </span>
                            <span className="text-xs text-slate-500">
                              {fw.docCount} templates
                            </span>
                          </div>
                        </Link>

                        <div className="shrink-0">
                          {isActive ? (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-brand-light flex items-center gap-1">
                                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                  <polyline points="20,6 9,17 4,12" />
                                </svg>
                                Active
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                loading={isToggling}
                                onClick={() => handleToggle(fw.id)}
                              >
                                Deactivate
                              </Button>
                            </div>
                          ) : cannotActivate ? (
                            <span
                              className="text-xs text-slate-500 cursor-help"
                              title="Upgrade plan for more frameworks"
                            >
                              Limit reached
                            </span>
                          ) : (
                            <Button
                              variant="secondary"
                              size="sm"
                              loading={isToggling}
                              onClick={() => handleToggle(fw.id)}
                            >
                              Activate
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
}
